import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
} from '@dnd-kit/core'
import { PropsWithChildren, useContext, useMemo } from 'react'
import { ContentType } from '@bm-js/atlascloud-shared'
import { DnDActionType } from '../reducers/DragNDrop.reducer'
import ContentTypeDragItem from './ContentTypeDragItem'
import { initialDndState } from '../utils/dnd.utils'
import { DragNDropContext } from '../contexts/DragNDrop.context'
import { useKeyPress } from '../hooks/useKeyPress'
import { EditorDroppbleData } from './EditorDroppable'
import { DraggableData } from './Draggable'
import { onDragEnd } from '../utils/editor.utils'
import { useCustomer, useEditorActions, useEditorData } from '../utils/hooks'

const DnDWrapper = ({ children }: PropsWithChildren) => {
  const { dispatch: dndDispatch, state: dndState } =
    useContext(DragNDropContext)
  const { activePage, workingConfig } = useEditorData()
  const { patchActivePage } = useEditorActions()
  const customer = useCustomer({ required: true })

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  })

  const handleDragEnd = (e: DragEndEvent) => {
    dndDispatch({
      type: DnDActionType.replaceState,
      data: initialDndState,
    })
    const { over, active } = e
    if (!over?.data.current || !active.data.current || !activePage) return
    const destination = over.data.current as EditorDroppbleData
    const source = active.data.current as DraggableData
    const updatedPage = onDragEnd({
      source,
      destination,
      page: activePage,
      websiteConfig: workingConfig,
      customerName: customer.customerName,
    })
    patchActivePage(updatedPage)
  }

  const handleDragStart = (e: DragStartEvent) => {
    if (!e.active.data.current) return
    dndDispatch({
      type: DnDActionType.replaceState,
      data: {
        currentDraggingContent: e.active.data.current as ContentType<unknown>,
      },
    })
  }

  const activeItem = useMemo(() => {
    if (!dndState.currentDraggingContent) return null
    return dndState.currentDraggingContent
  }, [dndState])

  useKeyPress({
    key: 'Escape',
    handler: () => {
      dndDispatch({
        type: DnDActionType.replaceState,
        data: initialDndState,
      })
    },
    condition: !!dndState.currentDraggingContent,
  })

  return (
    <DndContext
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={[mouseSensor]}
    >
      {children}
      <DragOverlay zIndex={1000} dropAnimation={null}>
        {activeItem && <ContentTypeDragItem content={activeItem} />}
      </DragOverlay>
    </DndContext>
  )
}

export default DnDWrapper
